import React, { useEffect, useState } from 'react'
import { Col, Form, Row } from 'react-bootstrap'
import { addDays, format } from 'date-fns'
import { useDispatch } from 'react-redux'

import useTranslation from '@/Hooks/UseTranslation'
import { useProgrammation } from '@/Hooks/UseProgrammation'
import { useProject } from '@/Hooks/UseProject'
import { useDatePicker } from '@/Hooks/UseDatePicker'

import { updateTaskAction } from '@/Redux/Actions/ProgrammationManagerActions'
import { updateTaskBaseDates } from '@/Services/ProgrammationManagerService'

import InputText from '../../components/InputText'

const parseLocalDate = (dateStr: string) => {
  if (!dateStr) return null

  const [ y, m, dPart ] = dateStr.split('-')
  const d = dPart?.split(' ')[0]

  const year = Number(y)
  const month = Number(m)
  const day = Number(d)

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null
  }

  const date = new Date(year, month - 1, day)

  return isNaN(date.getTime()) ? null : date
}

const safeFormat = (dateStr: string, pattern: string) => {
  const date = parseLocalDate(dateStr)
  return date ? format(date, pattern) : ''
}

const TaskBaseDates = () => {

  const { t } = useTranslation()

  const dispatch = useDispatch()

  const { itemOpened: task, patchItemOpened, setEditingItem } = useProgrammation()
  const { project, nonWorkingDays } = useProject()

  const { showDateRangePicker, hideDatePicker } = useDatePicker()

  const [ baseStart, setBaseStart ] = useState('')
  const [ baseEnd, setBaseEnd ] = useState('')
  const [ baseDaysReset, setBaseDaysReset ] = useState(0)

  useEffect(() => {
    setBaseStart(safeFormat(task.baseStart || task.startsAt, 'yyyy-MM-dd'))
    setBaseEnd(safeFormat(task.baseEnd || task.endsAt, 'yyyy-MM-dd'))
  }, [task.baseStart, task.baseEnd])

  const saveChanges = async (baseStart: string, baseEnd: string) => {
    setEditingItem(true)

    const [ error, data ] = await updateTaskBaseDates(task._id, baseStart, baseEnd)

    setEditingItem(false)

    if (error) {
      return false
    }

    patchItemOpened({
      baseStart: data.task.baseStart,
      baseEnd: data.task.baseEnd,
      baseDays: data.task.baseDays,
    })

    await dispatch(updateTaskAction(data.task, data.updatedTasks))
  }

  const handleChangeDates = async (dates: any) => {

    const {
      startDate,
      endDate
    } = dates

    setBaseStart(startDate)
    setBaseEnd(endDate)

    await saveChanges(startDate, endDate)

    hideDatePicker()
  }

  const handleShowDateRangePicker = () => {

    if (task.hasSubTasks) {
      return
    }

    showDateRangePicker({
      startDate: baseStart,
      endDate: baseEnd,
      projectStartDate: project.startsAt,
      projectEndDate: project.endsAt,
      nonWorkingDays,
      onChangeRange: handleChangeDates
    })
  }

  const handleChangeBaseDays = async (value: string) => {

    if (task.hasSubTasks) {
      return
    }

    const newBaseDays = parseInt(value, 10)

    if (isNaN(newBaseDays) || newBaseDays < 1) {
      setBaseDaysReset(t => t + 1)
      return
    }

    const newBaseEnd = format(addDays(parseLocalDate(baseStart) as Date, newBaseDays - 1), 'yyyy-MM-dd')

    setBaseEnd(newBaseEnd)

    await saveChanges(baseStart, newBaseEnd)
  }

  return (
    <>
      <Row>
        <Col>
          <h5 className='group-title'>
            {t('PROGRAMMATION_MANAGER:BaseDatesTitleLabel')}
          </h5>
        </Col>
      </Row>
      <Row>

        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseStartLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseStart, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseEndLabel')}
            </Form.Label>
            <InputText
              value={safeFormat(baseEnd, 'dd/MM/yyyy')}
              placeholder="dd/mm/yyyy"
              onClick={handleShowDateRangePicker}
              asButton
            />
          </Form.Group>
        </Col>
        <Col sm={4}>
          <Form.Group>
            <Form.Label>
              {t('PROGRAMMATION_MANAGER:BaseDaysLabel')}
            </Form.Label>
            <InputText
              key={`baseDays-${baseDaysReset}`}
              value={task.baseDays || task.executeDays}
              onChange={handleChangeBaseDays}
              style={{
                padding: '0.375rem 0.75rem !important'
              }}
            />
          </Form.Group>
        </Col>
      </Row>
    </>
  )
}

export default TaskBaseDates